import * as React from 'react'
import classNames from 'classnames'

import { renderCatch } from '../error/renderCatch'
import type { CommonDeps } from '../base/CommonDeps'
import { SVG_Exit } from '../svg/SVG_Exit'
import { BrowserPhotoCapture } from '../photo/BrowserPhotoCapture'
import { type PromiseState, initialPromiseState, promiseStateCancel, promiseStateTrigger } from '../util/PromiseState'
import type { DeviceIndexLookup } from '../photo/DeviceIndexLookup'
import i18next from 'i18next'
import type { CameraType } from '../../../shared/domain/flows/types.no-deps'
import { Spinner } from '../../../app-core/lib/ui/Spinner'

interface Props {
    deps: CommonDeps
    onClose: () => void
    savePhoto: (blob: Blob) => Promise<void>
    browserCameraDeviceIndexLookup: DeviceIndexLookup
    mode?: CameraType
    videoTrackConstraints?: MediaTrackConstraints
    isAndroid?: boolean
}

interface State {
    saveState: PromiseState<void>
}

export class CameraOverlay extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)
    public state: State = { saveState: initialPromiseState }
    public componentWillUnmount = () => promiseStateCancel(this.state.saveState)

    private tryRender = () => {
        const saveStatus = this.state.saveState.status
        return (
            <BrowserPhotoCapture
                deps={this.props.deps}
                onFailure={() => null}
                browserCameraDeviceIndexLookup={this.props.browserCameraDeviceIndexLookup}
                mode={this.props.mode}
                videoTrackConstraints={this.props.videoTrackConstraints}
            >
                {({ videoElement, toggleCamera, devices, takePhoto, recordVideo, stopRecording, recording }) => (
                    <zaptic-modal z-index={4}>
                        <div className="AppCore__flow__CameraOverlay">
                            <div className="AppCore__flow__CameraOverlay__close" onClick={this.props.onClose}>
                                <SVG_Exit color="white" />
                            </div>
                            {(this.props.mode === 'video' &&
                                this.VideoCam(
                                    saveStatus,
                                    videoElement,
                                    recording,
                                    recordVideo,
                                    stopRecording,
                                    devices,
                                    toggleCamera
                                )) ||
                                this.PhotoCam(saveStatus, videoElement, recording, takePhoto, devices, toggleCamera)}
                        </div>
                    </zaptic-modal>
                )}
            </BrowserPhotoCapture>
        )
    }

    private PhotoCam(
        saveStatus: 'not-triggered' | 'in-progress' | 'rejected' | 'resolved',
        videoElement: React.ReactElement<unknown, string | React.JSXElementConstructor<any>>,
        recording: boolean,
        takePhoto: (targetDimension: number) => Promise<Blob>,
        devices: MediaDeviceInfo[],
        toggleCamera: () => void
    ) {
        return (
            <>
                {/* Fixes the in-app camera video preview getting frozen on some
                    Android devices while no touch input is given. Chromium issue 1401352 */}
                {this.props.isAndroid && (
                    <div className="AppCore__flow__CameraOverlay__androidFix">
                        <Spinner size={1} />
                    </div>
                )}

                {saveStatus === 'in-progress' && (
                    <div className="AppCore__flow__CameraOverlay__message">
                        {i18next.t('appcore:CameraOverlay.saving')}
                    </div>
                )}
                {saveStatus === 'rejected' && (
                    <div className="AppCore__flow__CameraOverlay__message">
                        {i18next.t('appcore:CameraOverlay.failed')}
                    </div>
                )}
                {saveStatus === 'not-triggered' && (
                    <div className={classNames('AppCore__flow__CameraOverlay__video', { recording })}>
                        {videoElement}
                    </div>
                )}
                {saveStatus === 'not-triggered' && recording && (
                    <div className="AppCore__flow__CameraOverlay__buttons">
                        {devices.length > 1 && (
                            <div className="AppCore__flow__CameraOverlay__button" onClick={toggleCamera}>
                                {i18next.t('appcore:CameraOverlay.toggleCamera')}
                            </div>
                        )}
                        <div
                            className="AppCore__flow__CameraOverlay__button"
                            onClick={() => {
                                promiseStateTrigger(this.props.deps, this, 'saveState', async () => {
                                    const blob = await takePhoto(750)
                                    await this.props.savePhoto(blob)
                                })
                            }}
                        >
                            {i18next.t('appcore:CameraOverlay.takePhoto')}
                        </div>
                    </div>
                )}
            </>
        )
    }

    private VideoCam(
        saveStatus: 'not-triggered' | 'in-progress' | 'rejected' | 'resolved',
        videoElement: React.ReactElement<unknown, string | React.JSXElementConstructor<any>>,
        recording: boolean,
        recordVideo: () => Promise<Blob>,
        stopRecording: () => void,
        devices: MediaDeviceInfo[],
        toggleCamera: () => void
    ) {
        return (
            <>
                {saveStatus === 'rejected' && (
                    <div className="AppCore__flow__CameraOverlay__message">
                        {i18next.t('appcore:CameraOverlay.failed')}
                    </div>
                )}
                {(saveStatus === 'not-triggered' || saveStatus === 'in-progress') && (
                    <div className={classNames('AppCore__flow__CameraOverlay__video', { recording })}>
                        {videoElement}
                    </div>
                )}
                {(saveStatus === 'not-triggered' || saveStatus === 'in-progress') && recording && (
                    <div className="AppCore__flow__CameraOverlay__buttons">
                        {devices.length > 1 && (
                            <div className="AppCore__flow__CameraOverlay__button" onClick={toggleCamera}>
                                {i18next.t('appcore:CameraOverlay.toggleCamera')}
                            </div>
                        )}
                        <div
                            className="AppCore__flow__CameraOverlay__button"
                            onClick={() => {
                                promiseStateTrigger(this.props.deps, this, 'saveState', async () => {
                                    let blob
                                    if (saveStatus === 'not-triggered') {
                                        blob = await recordVideo()
                                        await this.props.savePhoto(blob)
                                    }
                                    if (saveStatus === 'in-progress') {
                                        stopRecording()
                                    }
                                })
                            }}
                        >
                            {saveStatus === 'not-triggered' && i18next.t('appcore:CameraOverlay.recordVideo')}
                            {saveStatus === 'in-progress' && i18next.t('appcore:CameraOverlay.stopRecording')}
                        </div>
                    </div>
                )}
            </>
        )
    }
}
